import React, { useState } from "react";
import DataTable from "../../Components/ui/dataTable";
import { Button } from "../../Components/ui/button";
import { NavLink } from "react-router-dom";
import { useGetAllRidersQuery } from "./RiderApi";
import { format, parseISO } from "date-fns";
import { SkeletonTable } from "../../Components/ui/skeleton";
import { ProtectedRoutes } from "../../config/protectedRoutes";
import Pagination from "../../Components/ui/pagination";
import { images } from "../../Assets";
import { useDebouncedValue } from "../../Lib/hooks";
import { DatePickerWithRange } from "../../Components/ui/dateRange";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../../Components/ui/select";

export const columns = [
  // {
  //   accessorKey: "no",
  //   header: "No",
  //   cell: ({ row }) => <div>{row.getValue("no")}</div>,
  // },
  {
    accessorKey: "name",
    header: "Name",
    cell: ({ row }) => (
      <div className='font-semibold'>{row.getValue("name")}</div>
    ),
  },
  {
    accessorKey: "createdAt",
    header: "Reg. Date",
    cell: ({ row }) => (
      <div className='capitalize'>
        {format(parseISO(row.getValue("createdAt")), "LLL dd, y")}
      </div>
    ),
  },
  {
    accessorKey: "phoneNumber",
    header: "Phone Number",
    cell: ({ row }) => (
      <div className='lowercase'>{row.getValue("phoneNumber")}</div>
    ),
  },
  {
    accessorKey: "email",
    header: "Email",
    cell: ({ row }) => <div className='lowercase'>{row.getValue("email")}</div>,
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }) => {
      const s = row.original?.status;
      const variant =
        s === "approved"
          ? "successful"
          : s === "declined"
            ? "failed"
            : "pending";
      const label =
        s === "approved" ? "Active" : s === "declined" ? "Declined" : "Pending";
      return (
        <Button asChild className='w-full' variant={variant}>
          <p>{label}</p>
        </Button>
      );
    },
  },
  {
    id: "action",
    cell: ({ row }) => {
      const details = row.original;
      return (
        <NavLink
          to={`/riders/profile/${details.id}`}
          className='whitespace-nowrap border border-primaryGreen text-primaryGreen p-2 rounded-md font-medium'
        >
          View Details
        </NavLink>
      );
    },
  },
];
const Riders = () => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [date, setDate] = useState({ from: null, to: null });
  const debouncedSearch = useDebouncedValue(search, 500);

  const hasActiveFilters =
    search !== "" || status !== "" || date.from !== null || date.to !== null;

  const clearFilters = () => {
    setSearch("");
    setStatus("");
    setDate({ from: null, to: null });
    setPage(1);
  };

  const { data, isLoading: loading } = useGetAllRidersQuery(
    {
      page,
      searchValue: debouncedSearch,
      status,
      startDate: date?.from ? format(date.from, "yyyy-MM-dd") : "",
      endDate: date?.to ? format(date.to, "yyyy-MM-dd") : "",
    },
    {
      refectchOnFocus: true,
      refetchOnReconnect: true,
      refetchOnMountOrArgChange: true,
    },
  );

  return (
    <div className='bg-white p-4'>
      <div className='mb-1'>
        <h2 className='text-2xl font-bold text-primaryBlack'>Riders</h2>
        <p className='text-sm text-[#667499]'>
          Review, approve and manage rider accounts
        </p>
      </div>
      <div className='flex flex-wrap justify-between items-center gap-3 my-4'>
        <div className='flex flex-wrap items-center gap-3'>
          <div className='flex items-center bg-[#F5FEF1] w-full sm:w-[300px] p-1 rounded-sm'>
            <input
              id='firstName'
              className='bg-transparent w-full px-1 outline-none placeholder:text-sm'
              value={search}
              placeholder='Search'
              onChange={(e) => setSearch(e.target.value)}
            />
            <img src={images.search} className='h-5 w-5' />
          </div>

          <Select
            value={status === "" ? "all" : status}
            onValueChange={(val) => {
              setStatus(val === "all" ? "" : val);
              setPage(1);
            }}
          >
            <SelectTrigger className='w-[150px] text-sm'>
              <SelectValue placeholder='All Approval' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='all'>All Approval</SelectItem>
              <SelectItem value='pending'>Pending</SelectItem>
              <SelectItem value='approved'>Approved</SelectItem>
              <SelectItem value='declined'>Declined</SelectItem>
            </SelectContent>
          </Select>

          <DatePickerWithRange date={date} setDate={setDate} />

          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className='text-sm text-red-500 hover:text-red-700 underline whitespace-nowrap'
            >
              Clear Filters
            </button>
          )}
        </div>
        {data?.total > 0 && (
          <p className='text-bold text-[#667499]'>
            Total: <span className='text-primaryBlack'>{data?.total}</span>
          </p>
        )}
      </div>
      <div className='table-scroll max-h-[58vh] overflow-auto max-w-full rounded-md border'>
        <div className='w-full'>
          {loading ? (
            <SkeletonTable />
          ) : (
            <DataTable columns={columns} data={data?.data} />
          )}
        </div>
      </div>
      {!loading && (
        <Pagination
          page={page}
          limit={data?.limit ?? 0}
          total={data?.total ?? 0}
          setPage={setPage}
        />
      )}
    </div>
  );
};

export default ProtectedRoutes(Riders, [4, 2, 1]);
